import * as React from 'react';
import { createContainer } from 'react-tracked';
import { INTEGRATION, PRODUCTION } from '@sites/constants/env-variables';
import { RESI_TEST_APP_BUILD_ENVS } from '@sites/constants/resi-test-app';

type EnvContextState = {
  gatewayUrl: string;
  resiStorageKey: string;
  sitesClientUrl: string;
  sitesUrl: string;
  webplayerEnvAttribute: string;
  webplayerLoader: string;
  webplayerPageScript: string;
};

type EnvContextHook = EnvContextState & {
  API_GATEWAY_V1: string;
  SITES_API_V1: string;
  setBuildEnvironment: (buildEnv: string) => void;
};

enum ENV_CONTEXT_ACTION_TYPES {
  SET_BUILD_ENVIRONMENT,
}

type EnvActions = {
  payload: string;
  type: ENV_CONTEXT_ACTION_TYPES.SET_BUILD_ENVIRONMENT;
};

const configContextReducer = (state: EnvContextState, action: EnvActions) => {
  switch (action.type) {
    case ENV_CONTEXT_ACTION_TYPES.SET_BUILD_ENVIRONMENT:
      if (action.payload === RESI_TEST_APP_BUILD_ENVS.INTEGRATION) {
        return {
          ...state,
          ...INTEGRATION,
        };
      } else {
        return {
          ...state,
          ...PRODUCTION,
        };
      }
    default:
      return state;
  }
};

const useValues = (props: React.PropsWithChildren<EnvContextState>): readonly [EnvContextHook, () => void] => {
  const [
    {
      gatewayUrl,
      resiStorageKey,
      sitesClientUrl,
      sitesUrl,
      webplayerEnvAttribute,
      webplayerLoader,
      webplayerPageScript,
    },
    dispatch,
  ] = React.useReducer(configContextReducer, props);

  const setBuildEnvironment = React.useCallback(
    (buildEnv: string) =>
      dispatch({
        type: ENV_CONTEXT_ACTION_TYPES.SET_BUILD_ENVIRONMENT,
        payload: buildEnv,
      }),
    []
  );

  const environment = React.useMemo(() => {
    return {
      API_GATEWAY_V1: `${gatewayUrl}/v1`,
      resiStorageKey,
      SITES_API_V1: `${sitesUrl}/api/v1`,
      setBuildEnvironment,
      sitesClientUrl,
      sitesUrl,
      gatewayUrl,
      webplayerEnvAttribute,
      webplayerLoader,
      webplayerPageScript,
    };
  }, [
    gatewayUrl,
    resiStorageKey,
    setBuildEnvironment,
    sitesClientUrl,
    sitesUrl,
    webplayerEnvAttribute,
    webplayerLoader,
    webplayerPageScript,
  ]);

  return [
    environment,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: EnvironmentProvider,
  useTrackedState: useEnvironment,
  useUpdate: useEnvironmentUpdate,
} = createContainer<EnvContextHook, () => void, React.PropsWithChildren<EnvContextState>>(useValues);

export { EnvironmentProvider, useEnvironment, useEnvironmentUpdate };
export type { EnvContextHook };
